import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useParams, Link } from "react-router-dom";
import { fmtNL } from "@/lib/dates";
import { ArrowLeft, MapPin, Phone, User as UserIcon, Palette, Trash2, Edit2, Camera, X } from "lucide-react";
import { toast } from "sonner";
import { ProjectModal, ProjectHoursDropdown, ProjectCostsSection, downloadCostReport } from "@/pages/Projecten";

const STATUS_LABELS = {
  offerte: "Offerte", gepland: "Gepland", in_uitvoering: "In uitvoering",
  opgeleverd: "Opgeleverd", afgerond: "Afgerond",
};

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get("/assignments", { params: { project_id: id } }),
      ]);
      setProject(p.data);
      setAssignments(a.data);
    } catch { toast.error("Project niet gevonden"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    if (window.confirm("Exporteer eerst de kosten van dit project, anders ben je ze kwijt. Nu exporteren?")) {
      await downloadCostReport(id, project.name);
    }
    if (!window.confirm("Project, planningen en kostenregels definitief verwijderen?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Verwijderd");
    window.history.back();
  };

  if (!project) return <div className="p-8 font-semibold">Laden…</div>;

  const plannedHours = assignments.reduce((sum, a) => {
    try {
      const [sh, sm] = (a.start_time||"07:00").split(":").map(Number);
      const [eh, em] = (a.end_time||"16:00").split(":").map(Number);
      return sum + (((eh*60+em - sh*60-sm)/60) * (a.members?.length || 0));
    } catch { return sum; }
  }, 0);

  const pct = project.budget_hours ? Math.min(100, (plannedHours / project.budget_hours) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8">
      <Link to="/projecten" className="text-sm font-bold uppercase tracking-widest text-slate-600 flex items-center gap-1 mb-3"><ArrowLeft size={16}/> Terug</Link>
      <div className="pb-card overflow-hidden">
        <div className="h-3" style={{ background: project.color }}/>
        <div className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{project.project_number}</div>
              <h1 className="pb-heading text-3xl font-black">{project.name}</h1>
              <div className="text-slate-700 font-semibold mt-1">{project.customer_name}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-white font-black uppercase text-xs px-3 py-2 rounded" style={{ background: project.color }}>{STATUS_LABELS[project.status]}</div>
              <button onClick={() => setEditing(true)} className="pb-btn-outline !min-h-[40px] flex items-center gap-1" data-testid="btn-edit-project"><Edit2 size={16}/> Bewerken</button>
              <button onClick={remove} className="pb-btn-outline !min-h-[40px] !border-red-600 !text-red-600" data-testid="btn-delete-project"><Trash2 size={16}/></button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <InfoRow icon={MapPin} title="Adres" value={fmtAddress(project)}/>
            <InfoRow icon={UserIcon} title="Contactpersoon" value={project.contact_person}/>
            <InfoRow icon={Phone} title="Telefoon" value={project.contact_phone}/>
            <InfoRow icon={Palette} title="Kleur planbord" value={project.color}/>
          </div>
          {project.description && (
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Omschrijving</div>
              <div className="font-semibold whitespace-pre-wrap">{project.description}</div>
            </div>
          )}
          {(project.parking_info || project.access_info) && (
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {project.parking_info && <div className="border-2 border-slate-900 rounded p-3"><div className="text-xs font-bold uppercase tracking-widest text-slate-500">Aanrij / Parkeren</div><div className="font-semibold">{project.parking_info}</div></div>}
              {project.access_info && <div className="border-2 border-slate-900 rounded p-3"><div className="text-xs font-bold uppercase tracking-widest text-slate-500">Toegang</div><div className="font-semibold">{project.access_info}</div></div>}
            </div>
          )}
          {project.photos?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-2"><Camera size={14}/> Foto&apos;s ({project.photos.length})</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="project-photos-gallery">
                {project.photos.map((src, i) => (
                  <button key={`${i}-${src.length}-${src.slice(-40)}`} onClick={() => setLightbox(src)} className="border-2 border-slate-900 rounded overflow-hidden" data-testid={`project-photo-${i}`}>
                    <img src={src} alt="" className="w-full h-32 object-cover"/>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-6">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Uren voortgang</div>
            <div className="w-full bg-slate-200 h-4 border-2 border-slate-900 rounded">
              <div className="h-full bg-orange-600" style={{ width: `${pct}%` }}/>
            </div>
            <div className="text-sm font-bold mt-1">{plannedHours.toFixed(0)} / {project.budget_hours || 0} uur ({pct.toFixed(0)}%)</div>
          </div>

          <div className="mt-6">
            <div className="pb-heading text-lg font-black mb-2">Gewerkte uren op dit project</div>
            <div className="pb-card overflow-hidden">
              <ProjectHoursDropdown projectId={id} />
            </div>
          </div>

          <div className="mt-6" data-testid="project-detail-costs">
            <ProjectCostsSection projectId={id} projectName={project.name} />
          </div>

          <div className="mt-6">
            <div className="pb-heading text-lg font-black mb-2">Planning ({assignments.length})</div>
            <div className="space-y-2">
              {assignments.slice(0, 20).map((a) => (
                <div key={a.id} className="flex items-center justify-between border-2 border-slate-900 rounded p-2">
                  <div>
                    <div className="font-bold">{fmtNL(a.date)}</div>
                    <div className="text-xs font-semibold text-slate-600">{a.start_time}-{a.end_time} · {a.members?.length || 0} pers.</div>
                  </div>
                  <div className="text-xs font-black uppercase text-white px-2 py-1 rounded" style={{ background: statusColor(a.status) }}>{a.status}</div>
                </div>
              ))}
              {assignments.length === 0 && <div className="text-sm text-slate-500">Nog geen planning</div>}
            </div>
          </div>
        </div>
      </div>
      {editing && (
        <ProjectModal
          existing={project}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="project-lightbox">
          <button className="absolute top-3 right-3 text-white bg-slate-900/70 rounded-full p-2 border-2 border-white z-10" onClick={() => setLightbox(null)}><X size={24}/></button>
          <img src={lightbox} alt="" className="w-full max-w-4xl max-h-[85vh] object-contain rounded"/>
        </div>
      )}
    </div>
  );
}

function fmtAddress(project) {
  const addr = (project.address || "").trim();
  const hn = (project.house_number || "").trim();
  const pc = (project.postcode || "").trim();
  const city = (project.city || "").trim();
  const first = hn && !new RegExp(`\\b${hn}\\b\\s*$`).test(addr) ? `${addr} ${hn}` : addr;
  return [first, `${pc} ${city}`.trim()].filter(Boolean).join(", ");
}

function statusColor(s) {  switch (s) {
    case "gepland": return "#64748B";
    case "bevestigd": return "#3B82F6";
    case "gestart": return "#F97316";
    case "afgerond": return "#16A34A";
    default: return "#64748B";
  }
}

function InfoRow({ icon: Icon, title, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={18} className="text-slate-700 mt-1"/>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{title}</div>
        <div className="font-semibold">{value || "—"}</div>
      </div>
    </div>
  );
}
